(() => {
  const SQUIGGLE_SVG = `
        <svg class="icon-squiggle-underline" viewBox="-347 -30.1947 694 96.19" stroke="currentColor" fill="none" role="presentation" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-width="20" pathLength="1" d="M-335,54 C-335,54 -171,-58 -194,-3 C-217,52 -224.1199951171875,73.552001953125 -127,11 C-68,-27 -137,50 -33,42 C31.43899917602539,37.042999267578125 147.14700317382812,-29.308000564575195 335,2"></path>
        </svg>
      `;

  const initializedRoots = new WeakSet();
  const cleanupsBySectionId = new Map();

  class PromoBarSlider {
    #slides;
    #total;
    #index = 0;
    #speed;
    #autoplay;
    #timer = null;
    #onAfterSlideChange;

    constructor(container, onAfterSlideChange) {
      this.#slides = Array.from(container.querySelectorAll('.promo-bar__slide'));
      this.#total = this.#slides.length;
      this.#onAfterSlideChange = onAfterSlideChange;
      if (this.#total <= 0) return;

      this.#speed = parseInt(container.dataset.speed, 10) || 5000;
      this.#autoplay = container.dataset.autoplay === 'true';

      this.#init();
    }

    get autoplay() {
      return this.#autoplay;
    }

    get total() {
      return this.#total;
    }

    #init() {
      if (this.#autoplay && this.#total > 1) {
        this.start();
      }
    }

    goTo(nextIndex) {
      if (this.#total <= 1) return;
      const i = (nextIndex + this.#total) % this.#total;
      const current = this.#slides[this.#index];
      current.classList.remove('is-active');
      current.classList.add('is-down');

      this.#index = i;
      const next = this.#slides[this.#index];
      next.classList.remove('is-down');
      next.classList.add('is-active');

      if (typeof this.#onAfterSlideChange === 'function') {
        this.#onAfterSlideChange();
      }
    }

    start() {
      this.stop();
      this.#timer = setInterval(() => {
        this.goTo(this.#index + 1);
      }, this.#speed);
    }

    stop() {
      if (this.#timer) clearInterval(this.#timer);
    }
  }

  const processHighlights = (container) => {
    const slides = container.querySelectorAll('.promo-bar__slide');
    for (const slide of slides) {
      const textEl = slide.querySelector('.promo-bar__text-content');
      const word = (slide.dataset.highlightWord || '').trim();
      if (!textEl || !word) continue;

      let html = textEl.innerHTML;
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp('\\b(' + escapedWord + ')\\b', 'gi');
      html = html.replace(
        regex,
        '<span class="promo-bar__highlight">$1' + SQUIGGLE_SVG + '</span>'
      );
      textEl.innerHTML = html;
    }
  };

  const initPromoBarRoot = (root) => {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    const bar = root.querySelector('.promo-bar[data-section-id="' + sectionId + '"]');
    const sliderEl = root.querySelector('.promo-bar__slider[data-section-id="' + sectionId + '"]');

    if (!bar || !sliderEl) return;

    let sliderInstance = null;
    let scrollHandler = null;
    let resizeHandler = null;
    let resizeObserver = null;
    let barHoverAbort = null;

    const updateScrollPadding = () => {
      document.documentElement.style.scrollPaddingTop = bar.offsetHeight + 'px';
    };

    const syncSliderTrackHeight = () => {
      const slides = sliderEl.querySelectorAll('.promo-bar__slide');
      if (slides.length === 0) return;
      let max = 0;
      for (const el of slides) {
        max = Math.max(max, el.offsetHeight);
      }
      if (max > 0) {
        sliderEl.style.minHeight = max + 'px';
      }
    };

    const teardownListeners = () => {
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      if (barHoverAbort) {
        barHoverAbort.abort();
        barHoverAbort = null;
      }
    };

    const afterSlideChange = () => {
      syncSliderTrackHeight();
      updateScrollPadding();
    };

    const init = () => {
      teardownListeners();

      processHighlights(sliderEl);
      syncSliderTrackHeight();
      updateScrollPadding();

      if (sliderInstance) {
        sliderInstance.stop();
      }
      sliderInstance = new PromoBarSlider(sliderEl, afterSlideChange);
      syncSliderTrackHeight();

      if (sliderInstance && sliderInstance.autoplay && sliderInstance.total > 1) {
        barHoverAbort = new AbortController();
        const hoverOpts = { signal: barHoverAbort.signal };
        bar.addEventListener(
          'mouseenter',
          () => {
            sliderInstance.stop();
          },
          hoverOpts
        );
        bar.addEventListener(
          'mouseleave',
          () => {
            sliderInstance.start();
          },
          hoverOpts
        );
      }

      const revealByDistance = bar.dataset.revealScroll === 'true';
      const revealAfterViewport = bar.dataset.revealAfterViewport === 'true';
      const revealAfterSlider = bar.dataset.revealAfterSlider === 'true';
      let threshold = parseInt(bar.dataset.scrollThreshold || '200', 10) || 200;

      const updateThreshold = () => {
        if (revealAfterViewport) {
          threshold = window.innerHeight;
          return;
        }
        if (revealAfterSlider) {
          const slider = document.querySelector('[data-section-type="slideshow-section"]');
          if (slider) {
            const rect = slider.getBoundingClientRect();
            threshold = rect.bottom + window.scrollY;
          }
        }
      };

      let isVisible = false;
      scrollHandler = function onPromoBarScroll() {
        if (!revealByDistance) return;
        
        if (!isVisible && window.scrollY > threshold + 20) {
          bar.classList.remove('promo-bar--scroll-hidden');
          isVisible = true;
        } else if (isVisible && window.scrollY < threshold - 20) {
          bar.classList.add('promo-bar--scroll-hidden');
          isVisible = false;
        }
      };

      if (revealByDistance) {
        updateThreshold();
        scrollHandler();
        window.addEventListener('scroll', scrollHandler, { passive: true });
      }

      resizeHandler = function onPromoBarResize() {
        updateThreshold();
        syncSliderTrackHeight();
        updateScrollPadding();
      };
      window.addEventListener('resize', resizeHandler);

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          syncSliderTrackHeight();
          updateScrollPadding();
        });
        resizeObserver.observe(bar);
      }
    };

    init();

    cleanupsBySectionId.set(sectionId, () => {
      teardownListeners();
      if (sliderInstance) sliderInstance.stop();
      initializedRoots.delete(root);
    });
  };

  const initAllPromoBars = (container = document) => {
    for (const root of container.querySelectorAll('.promo-bar-root[data-section-id]')) {
      initPromoBarRoot(root);
    }
  };

  if (!window.__promoBarGlobalListeners) {
    window.__promoBarGlobalListeners = true;
    document.addEventListener('shopify:section:load', (event) => {
      const sectionId = event.detail?.sectionId;
      if (!sectionId) return;
      const roots = event.target.querySelectorAll?.('.promo-bar-root[data-section-id]');
      if (!roots || roots.length === 0) return;

      const prevCleanup = cleanupsBySectionId.get(sectionId);
      if (prevCleanup) prevCleanup();
      cleanupsBySectionId.delete(sectionId);

      for (const root of roots) {
        if (root.dataset.sectionId !== sectionId) continue;
        initializedRoots.delete(root);
        initPromoBarRoot(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllPromoBars());
  } else {
    initAllPromoBars();
  }
})();
